import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, CanActivateFn } from '@angular/router';
import { ApiUrls, Doctypes } from '../apiurls';
import {
  LOCAL_STORAGE_KEYS,
  LocalStorageItemsService,
} from '../../services/local-storage-items.service';

const getCookie = (name: string): string => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return cookie ? cookie.split('=').slice(1).join('=') : '';
};

const getDecodedCookie = (name: string): string => {
  const value = getCookie(name);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const deleteCookie = (cookieName: string): void => {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};
// Added session user in localstorage to persist user data across refreshes and to avoid multiple cookie reads
export const authGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const localStorageItemsService = inject(LocalStorageItemsService);

  let company: any = null;
  let userDetails: any = null;

  localStorageItemsService
    .getItem$(LOCAL_STORAGE_KEYS.USERSCOMPANIES)
    .subscribe((data: any) => {
      company = data;
    });

  localStorageItemsService
    .getItem$(LOCAL_STORAGE_KEYS.LOGIN)
    .subscribe((data: any) => {
      userDetails = data;
    });

  const setupSsoUser = async (): Promise<boolean> => {
    const ssoUser = getDecodedCookie('sso_user').toLowerCase();
    if (ssoUser !== 'true') return false;

    const cookieValueFullName = getDecodedCookie('full_name');
    const cookieValueUserId = getDecodedCookie('user_id');
    const lowerCaseFullName = cookieValueFullName.toLowerCase();
    const hasValidUser =
      !!cookieValueUserId &&
      lowerCaseFullName !== 'guest' &&
      lowerCaseFullName !== 'administrator' &&
      lowerCaseFullName !== 'null' &&
      lowerCaseFullName !== '';

    if (!hasValidUser) {
      deleteCookie('sso_user');
      return false;
    }

    try {
      const permissionsRes: any = await http
        .get(ApiUrls.getUserPermissionsList)
        .toPromise();
      const companyData = permissionsRes?.message?.message;

      if (!permissionsRes?.message?.success || !companyData?.length) {
        deleteCookie('sso_user');
        router.navigate(['']);
        return true;
      }

      const userRes: any = await http
        .get(ApiUrls.get_by_name, {
          params: {
            doctype: Doctypes.ezy_employee,
            name: cookieValueUserId,
          },
        })
        .toPromise();

      if (!userRes?.message?.success) {
        deleteCookie('sso_user');
        router.navigate(['']);
        return true;
      }

      const employee = userRes?.message?.message;
      localStorageItemsService.setItem(LOCAL_STORAGE_KEYS.LOGIN, employee);
      localStorageItemsService.setItem(
        LOCAL_STORAGE_KEYS.USERSCOMPANIES,
        companyData
      );

      deleteCookie('sso_user');

      const path =
        employee?.designation === 'Caratred Supervisor'
          ? '/home/company'
          : '/home/products';
      router.navigate([path]);
      return true;
    } catch {
      deleteCookie('sso_user');
      router.navigate(['']);
      return true;
    }
  };

  if (await setupSsoUser()) {
    return false;
  }

  if (state.url === '/change-password') {
    // const nav = router.getCurrentNavigation();
    // const allow = nav?.extras?.state?.['allowChangePassword'];

    const allow = localStorage.getItem('allowChangePassword') === 'true';

    if (allow) return true;

    router.navigate(['']); // or login
    return false;
  }

  // Case 1: If navigating to root "/" or "/home"
  if (state.url === '/' || state.url === '/home') {
    console.log(company);
    if (company) {
      let route =
        userDetails.designation == 'Caratred Supervisor'
          ? ['/home/company']
          : ['/home/products'];
      router.navigate(route);
      return false;
    }
    return true;
  }

  if (state.url === '/change-password') {
    const nav = router.getCurrentNavigation();
    const allow = nav?.extras?.state?.['allowChangePassword'];

    if (allow) return true;

    router.navigate(['']); // or login
    return false;
  }

  // if (state.url === '/change-password') {
  //   return true;
  // }

  // Redirect non-supervisors away from company/modules to products
  if (
    (state.url === '/home/company' || state.url === '/home/modules') &&
    userDetails?.designation !== 'Caratred Supervisor'
  ) {
    router.navigate(['/home/products']);
    return false;
  }

  // Case 2: If no company and accessing any other route
  if (!company) {
    router.navigate(['']);
    return false;
  }

  // Optional: permission-based check
  const ignoringRoutes: string[] = [
    // '/home/user-management',
    // '/home/dashboard',
  ];

  const checkUserManagementRouting = async (url: string): Promise<boolean> => {
    const ignoreRouteExist = ignoringRoutes.some((r) => url.includes(r));
    if (ignoreRouteExist) return true;

    let userDetails: any = null;
    localStorageItemsService
      .getItem$(LOCAL_STORAGE_KEYS.LOGIN)
      .subscribe((data: any) => {
        userDetails = data;
      });
    if (userDetails) {
      try {
        const res: any = await http
          .get(ApiUrls.get_by_name, {
            params: {
              doctype: Doctypes.roles_permission,
              name: userDetails?.company_field + '-' + userDetails?.designation,
            },
          })
          .toPromise();

        return res?.data?.permission_list?.some(
          (el: any) => el?.route_link === url && el.read
        );
      } catch {
        return false;
      }
    }

    return false;
  };
  return checkUserManagementRouting(state.url.split('?')[0]);
};
